import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { useAppContext } from "@/context/AppContext";
import { useNavigation } from "@/context/NavigationContext";
import ExperienceCard from "@/components/ExperienceCard";
import InstrumentLoader from "@/components/InstrumentLoader";
import {
  Search,
  Filter,
  Compass,
  RotateCcw,
  MapPin,
  ChevronDown,
  X,
  Check,
  Activity
} from "lucide-react";

type SortOption = "price-asc" | "price-desc" | "title-asc";

interface CategoryOption {
  code: string;
  label: string;
}

const ExperienceCategoryPage = () => {
  const { navigateTo } = useNavigation();
  const { getDictValue, getDictJson } = useAuth();

  // 🟢 Extract experiences state, fetch function, and loader directly from AppContext
  const {
    experiences,
    fetchExperiences,
    isExperiencesLoading
  } = useAppContext();

  const [search, setSearch] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sort, setSort] = useState<SortOption>("price-asc");
  const [locationSearch, setLocationSearch] = useState("");
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);

  // 🟢 Preluare dinamică a categoriilor de experiențe din dicționar
  const experienceTypesList = useMemo(() => {
    return getDictJson<CategoryOption[]>("listing.experience.filter.categories", []);
  }, [getDictJson]);

  // 🟢 Fetch experiences on component mount when the page opens
  useEffect(() => {
    fetchExperiences().catch((error) => {
      console.error("Failed to fetch aviation experiences on page load:", error);
    });
  }, [fetchExperiences]);

  const filteredExperiences = useMemo(() => {
    if (isExperiencesLoading) return [];
    let result = [...experiences];

    // 1. Căutare textuală (Titlu, Descriere)
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        e.title?.toLowerCase().includes(q) ||
        e.description?.toLowerCase().includes(q)
      );
    }

    // 2. Filtru Categorie / Tip Experiență
    if (selectedTypes.length > 0) {
      result = result.filter(e => e.type && selectedTypes.includes(e.type));
    }

    // 3. Filtru Locație / Aerodrom
    if (locationSearch) {
      const loc = locationSearch.toLowerCase();
      result = result.filter(e => e.location?.toLowerCase().includes(loc));
    }

    // 4. Filtru Preț
    if (priceMin) result = result.filter(e => Number(e.grossValue || 0) >= Number(priceMin));
    if (priceMax) result = result.filter(e => Number(e.grossValue || 0) <= Number(priceMax));

    // 5. Sortare (Sortarea crescătoare trimite valorile nule/lipsă la final)
    switch (sort) {
      case "price-asc":
        result.sort((a, b) => {
          const valA = Number(a.grossValue);
          const valB = Number(b.grossValue);
          const pA = (!isNaN(valA) && valA > 0) ? valA : Number.MAX_VALUE;
          const pB = (!isNaN(valB) && valB > 0) ? valB : Number.MAX_VALUE;
          return pA - pB;
        });
        break;
      case "price-desc":
        result.sort((a, b) => {
          const valA = Number(a.grossValue);
          const valB = Number(b.grossValue);
          const pA = (!isNaN(valA) && valA > 0) ? valA : -1;
          const pB = (!isNaN(valB) && valB > 0) ? valB : -1;
          return pB - pA;
        });
        break;
      case "title-asc":
        result.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        break;
    }

    return result;
  }, [experiences, search, selectedTypes, locationSearch, priceMin, priceMax, sort, isExperiencesLoading]);

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 pb-20 font-sans">
      <InstrumentLoader visible={isExperiencesLoading} />

      <div className="container max-w-7xl py-12 px-6">
        {/* HEADER */}
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-l-4 border-red-600 pl-6">
          <div>
            <p className="text-red-500 font-mono text-sm tracking-[0.2em] mb-1 uppercase italic">
              {getDictValue("listing.experience.header") || "Booking Hub // Rezervă o Experiență"}
            </p>
            <h1 className="text-5xl font-black text-white tracking-tight italic uppercase">
              {getDictValue("listing.experience.title") || "EXPERIENȚE AVIATICE"}
            </h1>
          </div>
          <div className="flex items-center gap-4 bg-slate-900/50 p-2 rounded-2xl border border-slate-800">
            <div className="text-right px-4 border-r border-slate-800">
              <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                {getDictValue("listing.experience.filter.match.txb") || "Unități găsite"}
              </p>
              <p className="text-red-500 font-mono text-xl font-black">{filteredExperiences.length}</p>
            </div>
            <Compass className="text-slate-700 mr-2" size={20} />
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* ASIDE FILTERS */}
          <aside className="lg:col-span-3 space-y-6 sticky top-24 h-[calc(100vh-120px)] overflow-y-auto no-scrollbar pb-10">
            <div className="bg-[#0F172A]/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 shadow-2xl">

              <div className="flex items-center justify-between mb-6 border-b border-slate-800/50 pb-4">
                <div className="flex items-center gap-2">
                  <Filter size={16} className="text-red-500" />
                  <h3 className="text-xs font-black uppercase tracking-widest text-white">
                    {getDictValue("listing.experience.filter.title") || "Filtre"}
                  </h3>
                </div>
                {(search || selectedTypes.length > 0 || locationSearch || priceMin || priceMax) && (
                  <button
                    onClick={() => {
                      setSearch(""); setSelectedTypes([]); setLocationSearch(""); setPriceMin(""); setPriceMax("");
                    }}
                    className="text-[10px] text-red-500 font-bold uppercase flex items-center gap-1 active:scale-95 transition-all"
                  >
                    <RotateCcw size={12} /> Reset
                  </button>
                )}
              </div>

              <div className="space-y-8">
                {/* Search Text */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block ml-1">
                    {getDictValue("listing.experience.filter.id.txb") || "Căutare"}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                    <input
                      className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#1E293B] border border-slate-700 text-sm text-white focus:border-red-500 outline-none transition-all placeholder:text-slate-600"
                      placeholder={getDictValue("listing.experience.filter.id.placeholder") || "Ex: Salt parașută, Curs..."}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>

                {/* Location Filtering */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block ml-1">
                    {getDictValue("listing.experience.filter.location.txb") || "Locație / Aerodrom"}
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                    <input
                      className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#1E293B] border border-slate-700 text-sm text-white focus:border-red-500 outline-none transition-all placeholder:text-slate-600"
                      placeholder={getDictValue("listing.experience.filter.location.placeholder") || "Ex: Tuzla, Clinceni..."}
                      value={locationSearch}
                      onChange={(e) => setLocationSearch(e.target.value)}
                    />
                  </div>
                </div>

                {/* Experience Multi-Select Dropdown */}
                <div className="relative">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block ml-1">
                    {getDictValue("listing.experience.filter.category.txb") || "Categorie"}
                  </label>

                  <div
                    onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
                    className="min-h-[44px] w-full p-2 rounded-xl bg-[#1E293B] border border-slate-700 flex flex-wrap gap-1 items-center cursor-pointer hover:border-slate-600 transition-all relative pr-10"
                  >
                    {selectedTypes.length === 0 ? (
                      <span className="text-sm text-slate-500 ml-2 italic">Toate</span>
                    ) : (
                      selectedTypes.map(typeCode => {
                        const typeLabel = experienceTypesList.find(t => t.code === typeCode)?.label;
                        return (
                          <span
                            key={typeCode}
                            className="flex items-center gap-1 bg-red-600/20 border border-red-500/30 text-red-400 text-[10px] font-bold px-2 py-1 rounded-md animate-in zoom-in-95 duration-150"
                          >
                            {typeLabel}
                            <X
                              size={12}
                              className="hover:text-white transition-colors cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTypes(prev => prev.filter(t => t !== typeCode));
                              }}
                            />
                          </span>
                        );
                      })
                    )}

                    <ChevronDown
                      size={16}
                      className={`absolute right-3 text-slate-500 transition-transform duration-300 ${isTypeDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </div>

                  {/* Dropdown Popup */}
                  {isTypeDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsTypeDropdownOpen(false)} />

                      <div
                        className="absolute z-20 mt-2 w-full max-h-60 overflow-y-auto bg-[#0F172A] border border-slate-800 rounded-2xl shadow-2xl p-2 no-scrollbar"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {experienceTypesList.map((typeItem) => {
                          const isSelected = selectedTypes.includes(typeItem.code);
                          const count = experiences.filter(e => e.type === typeItem.code).length;

                          return (
                            <div
                              key={typeItem.code}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTypes(prev =>
                                  isSelected
                                    ? prev.filter(t => t !== typeItem.code)
                                    : [...prev, typeItem.code]
                                );
                              }}
                              className={`flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-colors mb-1 last:mb-0 ${
                                isSelected ? 'bg-red-500/10 text-red-400' : 'hover:bg-slate-800/50 text-slate-400'
                              }`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-all ${
                                  isSelected ? 'bg-red-500 border-red-500' : 'border-slate-600 bg-transparent'
                                }`}>
                                  {isSelected && <Check size={12} className="text-[#020617]" strokeWidth={4} />}
                                </div>
                                <span className="text-xs font-medium truncate">{typeItem.label}</span>
                              </div>
                              <span className="text-[10px] font-mono opacity-40 ml-2 flex-shrink-0">({count})</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>

                {/* Price Range */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block ml-1">
                    {getDictValue("listing.experience.filter.budget.txb") || "Buget"}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number" placeholder="Min €"
                      className="h-10 px-3 rounded-lg bg-[#1E293B] border border-slate-700 text-xs text-white outline-none focus:border-red-500"
                      value={priceMin} onChange={(e) => setPriceMin(e.target.value)}
                    />
                    <input
                      type="number" placeholder="Max €"
                      className="h-10 px-3 rounded-lg bg-[#1E293B] border border-slate-700 text-xs text-white outline-none focus:border-red-500"
                      value={priceMax} onChange={(e) => setPriceMax(e.target.value)}
                    />
                  </div>
                </div>

                {/* Sorting options */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block ml-1">
                    {getDictValue("listing.experience.filter.sort.txb") || "Sortare"}
                  </label>
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value as SortOption)}
                    className="w-full h-11 px-4 rounded-xl bg-[#1E293B] border border-slate-700 text-sm text-white focus:border-red-500 outline-none appearance-none cursor-pointer"
                  >
                    <option value="price-asc">
                      {getDictValue("listing.experience.filter.sort.cheapest") || "Preț: Crescător"}
                    </option>
                    <option value="price-desc">
                      {getDictValue("listing.experience.filter.sort.costliest") || "Preț: Descrescător"}
                    </option>
                    <option value="title-asc">Nume: A-Z</option>
                  </select>
                </div>

                {/* SUMAR REZULTATE */}
                <div className="pt-4 border-t border-slate-800/50">
                  <div className="bg-slate-950/50 rounded-2xl p-4 border border-slate-800 flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${filteredExperiences.length > 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                      <Activity size={16} className={filteredExperiences.length > 0 ? 'text-emerald-500' : 'text-red-500'} />
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 font-bold uppercase">
                        {getDictValue("listing.experience.filter.match.txb") || "Unități găsite"}
                      </p>
                      <p className="text-sm font-black text-white italic">
                        {filteredExperiences.length} {getDictValue("listing.experience.filter.match.results") || "Rezultate"}
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </aside>

          {/* MAIN GRID */}
          <main className="lg:col-span-9">
            {filteredExperiences.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-32 bg-slate-900/10 rounded-[3rem] border border-dashed border-slate-800">
                <Compass size={32} className="text-slate-700 mb-6" />
                <h4 className="text-white font-black uppercase tracking-widest mb-2">
                  {getDictValue("home.search.noResults") || "Nu s-au găsit anunțuri"}
                </h4>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                {filteredExperiences.map(experience => (
                  <div key={experience.experienceId || experience.title} className="transition-transform hover:scale-[1.02] active:scale-[0.98]">
                    <ExperienceCard experience={experience} />
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ExperienceCategoryPage;